import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import { OptionData } from '@atlaskit/user-picker';

import { ParticipantStateTypes, User } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import { getIsPullRequestAddReviewerPending } from 'src/redux/pull-request/selectors';
import { getPrReviewGroupsEnabled } from 'src/selectors/feature-selectors';

import { ConnectedExpander } from '../connected-expander';
import { ReviewGroups } from '../review-groups';

import { ReviewerSmartPicker } from './reviewer-smart-picker';
import ReviewerStatus from './reviewer-status';
import * as styles from './reviewer-status.style';
import messages from './reviewer-statuses-card.i18n';

export const REVIEWER_STATUSES_CARD_COLLAPSED_LOCALSTORAGE_KEY =
  'reviewer.statuses.card.collapsed';

type ReviewerStatusesCardProps = {
  isSidebarCollapsed?: boolean;
  onSelection: (user: OptionData) => void;
  onDelete: (userId: string) => void;
  reviewers: BB.PullRequestParticipant[];
  canAddReviewers: boolean;
  canDeleteReviewersOverride?: boolean;
  renderSecondaryIcon: () => JSX.Element;
  repoFullSlug: string;
  currentUser: User | null | undefined;
};

export const ReviewerStatusesCard = ({
  isSidebarCollapsed,
  onSelection,
  onDelete,
  reviewers,
  canAddReviewers,
  canDeleteReviewersOverride,
  renderSecondaryIcon,
  repoFullSlug,
  currentUser,
}: ReviewerStatusesCardProps) => {
  const intl = useIntl();

  const isPullRequestAddReviewerPending = useSelector(
    getIsPullRequestAddReviewerPending
  );

  const isPrReviewGroupsEnabled = useSelector(getPrReviewGroupsEnabled);

  const approvalsCount = reviewers.filter(
    participant => participant.state === ParticipantStateTypes.approved
  ).length;

  const icon = <PeopleIcon label="" size="medium" />;
  const labels = {
    rich: (
      <FormattedMessage
        {...messages.reviewerCount}
        values={{
          total: reviewers.length,
          formattedCount: <b>{reviewers.length}</b>,
        }}
      />
    ),
    plain: intl.formatMessage(messages.reviewerCount, {
      total: reviewers.length,
      formattedCount: reviewers.length,
    }),
  };

  if (isSidebarCollapsed) {
    return (
      <Tooltip position="left" content={labels.plain}>
        <Button appearance="subtle" iconBefore={icon} />
      </Tooltip>
    );
  }

  return (
    <ConnectedExpander
      localStorageKey={REVIEWER_STATUSES_CARD_COLLAPSED_LOCALSTORAGE_KEY}
      icon={icon}
      label={labels.rich}
      isPanelBodyOverflowVisible
      sectionAriaLabel={intl.formatMessage(messages.sectionAriaLabel)}
      headingAriaLabel={intl.formatMessage(messages.headingAriaLabel, {
        reviewersCount: reviewers.length,
        approvalsCount,
      })}
      renderSecondaryIcon={renderSecondaryIcon}
    >
      {reviewers.map((participant, index) => (
        <ReviewerStatus
          user={participant.user}
          state={participant.state}
          canDeleteReviewersOverride={canDeleteReviewersOverride}
          onDelete={onDelete}
          key={participant.user?.uuid || index}
        />
      ))}
      {isPrReviewGroupsEnabled && <ReviewGroups />}
      {canAddReviewers && (
        <>
          {isPullRequestAddReviewerPending && (
            <styles.SpinnerContainer>
              <Spinner size="medium" delay={0} />
            </styles.SpinnerContainer>
          )}
          {
            <ReviewerSmartPicker
              // @ts-ignore
              reviewersAccountIds={reviewers.map(
                reviewer => reviewer.user?.account_id
              )}
              onSelection={onSelection}
              repoFullSlug={repoFullSlug}
              currentUser={currentUser}
            />
          }
        </>
      )}
    </ConnectedExpander>
  );
};

export default React.memo(ReviewerStatusesCard);
